import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LocationPicker } from '../locations/LocationPicker';
import type { Answer, Question, QuestionKind } from './api';

const KINDS: QuestionKind[] = ['duplicate', 'package', 'manufacturer', 'location'];

interface Props {
  questions: Question[];
  busy: boolean;
  onAnswer: (answers: Answer[]) => void;
  onAnswerAll: (kind: QuestionKind, action: 'use' | 'skip') => void;
}

/**
 * One renderer for all four kinds of question.
 *
 * They differ in what they are about and not in what is being asked: here are
 * some candidates, one of them is pre-selected, and the alternatives are to make
 * a new one or to leave the row out. Four screens saying that four ways would be
 * four places to get the wording of "skip" subtly wrong.
 *
 * What does differ is scope, and that is shown rather than hidden: a duplicate
 * decides one row, a package decides every row that spelt it that way. Somebody
 * about to press "skip" on twelve rows should be able to see the twelve.
 */
export function QuestionList({ questions, busy, onAnswer, onAnswerAll }: Props) {
  const { t } = useTranslation();

  if (questions.length === 0) return null;

  const byKind = KINDS.map((kind) => ({
    kind,
    items: questions.filter((question) => question.kind === kind),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="imports__questions">
      {byKind.map((group) => (
        <section key={group.kind} className="panel">
          <header className="imports__group-head">
            <h3>{t(`imports.questions.${group.kind}.title`)}</h3>
            <div className="imports__group-actions">
              <button
                type="button"
                disabled={busy}
                onClick={() => onAnswerAll(group.kind, 'use')}
              >
                {t('imports.questions.acceptAll')}
              </button>
              <button
                type="button"
                className="link"
                disabled={busy}
                onClick={() => onAnswerAll(group.kind, 'skip')}
              >
                {t('imports.questions.skipAll')}
              </button>
            </div>
          </header>
          <p className="hint">{t(`imports.questions.${group.kind}.hint`)}</p>

          {group.items.map((question) => (
            <QuestionCard
              key={`${question.kind}:${question.key}`}
              question={question}
              busy={busy}
              onAnswer={onAnswer}
            />
          ))}
        </section>
      ))}
    </div>
  );
}

function QuestionCard({
  question,
  busy,
  onAnswer,
}: {
  question: Question;
  busy: boolean;
  onAnswer: (answers: Answer[]) => void;
}) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<string | null>(question.default_id);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState(question.query);
  const [parentId, setParentId] = useState<string | null>(null);

  const answer = (item: Answer) => onAnswer([item]);

  return (
    <article className="imports__question">
      <header>
        <strong>{question.query}</strong>
        {question.scope === 'value' && (
          <span className="badge">{t('imports.questions.rows', { count: question.rows })}</span>
        )}
      </header>

      {question.candidates.length === 0 && (
        <p className="hint">{t('imports.questions.nothingFound')}</p>
      )}

      {question.candidates.map((candidate) => (
        <label key={candidate.id} className="imports__candidate">
          <input
            type="radio"
            name={`${question.kind}:${question.key}`}
            checked={chosen === candidate.id}
            onChange={() => {
              setChosen(candidate.id);
              setCreating(false);
            }}
          />
          <span className="imports__candidate-label">{candidate.label}</span>
          {candidate.sub && <code>{candidate.sub}</code>}
          {candidate.details.length > 0 && <span className="hint">{candidate.details.join(' · ')}</span>}
          <span className="badge" title={t(`imports.reasons.${candidate.reason}`)}>
            {Math.round(candidate.score * 100)}
            {'%'}
          </span>
        </label>
      ))}

      {question.allow_create && (
        <label className="imports__candidate">
          <input
            type="radio"
            name={`${question.kind}:${question.key}`}
            checked={creating}
            onChange={() => {
              setCreating(true);
              setChosen(null);
            }}
          />
          <span className="imports__candidate-label">{t('imports.questions.createNew')}</span>
        </label>
      )}

      {creating && question.kind !== 'duplicate' && (
        <div className="imports__create">
          <label>
            {t('imports.questions.newName')}
            <input value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          {/* A place with no parent lands at the root of the tree, beside the
              building rather than inside the cabinet it belongs to. So the
              parent is asked for rather than defaulted. */}
          {question.kind === 'location' && (
            <LocationPicker
              label={t('imports.questions.newParent')}
              value={parentId}
              onChange={setParentId}
              allowOutside={false}
            />
          )}
        </div>
      )}

      <div className="imports__question-actions">
        <button
          type="button"
          disabled={busy || (creating ? (question.kind !== 'duplicate' && (!name.trim() || (question.kind === 'location' && !parentId))) : !chosen)}
          onClick={() =>
            answer(
              creating
                ? {
                    kind: question.kind,
                    key: question.key,
                    action: 'create',
                  payload: question.kind === 'duplicate' ? undefined : { name: name.trim(), parent_id: parentId },
                  }
                : { kind: question.kind, key: question.key, action: 'use', target_id: chosen },
            )
          }
        >
          {t('common.apply')}
        </button>
        <button
          type="button"
          className="link"
          disabled={busy}
          onClick={() => answer({ kind: question.kind, key: question.key, action: 'skip' })}
        >
          {t('imports.questions.skip')}
        </button>
      </div>
    </article>
  );
}
